import {Injectable} from '@angular/core';
import {BehaviorSubject, distinctUntilChanged, map, Observable} from 'rxjs';

export interface UploadProgress {
  percent: number;
  secondsLeft: number | null;
}

/**
 * State of requests that send files together with a save (comments, notes, tasks, materials, documents, drawings).
 * Filled by UploadProgressInterceptor. Such a request can take minutes on a slow connection and can't be cancelled
 * from the UI, so the app is blocked from interaction while it is in flight and its progress is shown to the user.
 */
@Injectable({providedIn: 'root'})
export class FileUploadService {
  private readonly activeUploads$ = new BehaviorSubject(0);
  private readonly progressSubject$ = new BehaviorSubject<UploadProgress | null>(null);

  readonly inProgress$: Observable<boolean> = this.activeUploads$.pipe(
    map(count => count > 0),
    distinctUntilChanged()
  );

  readonly progress$: Observable<UploadProgress | null> = this.progressSubject$.asObservable();

  get inProgress(): boolean {
    return this.activeUploads$.value > 0;
  }

  start(): void {
    this.activeUploads$.next(this.activeUploads$.value + 1);
    this.progressSubject$.next({percent: 0, secondsLeft: null});
  }

  reportProgress(loaded: number, total: number, startedAt: number): void {
    const elapsedSeconds = (Date.now() - startedAt) / 1000;
    const speed = elapsedSeconds > 0 ? loaded / elapsedSeconds : 0;

    this.progressSubject$.next({
      percent: Math.min(100, Math.round(loaded / total * 100)),
      secondsLeft: speed > 0 ? Math.ceil((total - loaded) / speed) : null
    });
  }

  finish(): void {
    const active = Math.max(0, this.activeUploads$.value - 1);
    this.activeUploads$.next(active);
    if (active === 0) this.progressSubject$.next(null);
  }
}
